import { gainXp } from '../realms';
import { UtilsManager } from '../utils';
import { explorationScenes, explorationEventResults, beastEncounterRates, LOCATION_NAMES } from './exploration-data.js';
import { generateRandomBeast } from './beast-data.js';
import BattleManager from './BattleManager.js';
import EventHandler from './EventHandler.js';

/**
 * 探索管理器 - 处理所有探索相关逻辑
 */
export default class ExplorationManager {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.eventHandler = new EventHandler(stateManager);
    this.battleManager = new BattleManager(stateManager);
    this.pendingBeastEncounter = null;
  }

  // 获取场景数据
  getLocationScene(location) {
    return explorationScenes[location] || this.getDefaultScene();
  }

  // 默认场景配置
  getDefaultScene() {
    return explorationScenes[LOCATION_NAMES.UNKNOWN];
  }

  // 初始化或更新探索状态
  startExploration() {
    const state = this.stateManager.getState();
    const scene = this.getLocationScene(state.location);

    if (!state.explorationState) {
      state.explorationState = this.createInitialState(scene);
    } else {
      this.updateExistingState(state.explorationState, scene);
    }

    this.stateManager.updateState({ explorationState: state.explorationState });
    return this.generateSceneMessages(state.explorationState);
  }

  // 创建初始探索状态
  createInitialState(scene) {
    const state = this.stateManager.getState();
    return {
      scene,
      discovered: [],
      collectedItems: {}, // 本次探索收集的可进入背包的物品 {itemName: count}
      clues: [],
      mood: 'curious',
      explorationCount: (state.explorationCount || 0) + 1,
      randomEventsTriggered: []
    };
  }

  // 更新现有状态
  updateExistingState(explorationState, scene) {
    explorationState.scene = scene;
    explorationState.mood = 'curious';
    explorationState.explorationCount++;
    // 注意：不重置collectedItems，保持本次探索的物品收集记录
  }

  // 生成场景描述消息
  generateSceneMessages(explorationState) {
    const scene = explorationState.scene;
    const messages = [];

    // 只在第一次探索时显示完整场景信息
    if (explorationState.explorationCount <= 1) {
      messages.push(`【${scene.name}】`);
      messages.push(scene.description);
    } else {
      // 后续探索只显示简要信息
      messages.push(`继续在【${scene.name}】探索...`);
    }

    // 显示新线索
    const newClues = explorationState.clues.filter(clue =>
      !explorationState.previousClues?.includes(clue)
    );

    if (newClues.length > 0) {
      messages.push('你发现了新的线索：');
      newClues.forEach(clue => messages.push(`• ${clue}`));
    }

    // 保存当前线索用于下次比较
    explorationState.previousClues = [...explorationState.clues];

    messages.push('你可以：');
    this.addActionMessages(messages, scene);
    messages.push('0. 结束探索，返回洞府 - explore 0');

    return messages;
  }

  // 添加操作消息
  addActionMessages(messages, scene) {
    const actions = scene.baseEvents || scene.actions?.map(a => a.description) || [];
    actions.forEach((action, idx) => {
      messages.push(`${idx + 1}. ${action} - explore ${idx + 1}`);
    });
  }

  // 检查并触发随机事件
  checkAndTriggerRandomEvent() {
    const state = this.stateManager.getState();
    if (!state.explorationState) {
      return null;
    }

    const randomEvent = this.eventHandler.getRandomEvent(state.location);
    if (randomEvent && !state.explorationState.randomEventsTriggered.includes(randomEvent.id)) {
      // 标记事件已触发
      state.explorationState.randomEventsTriggered.push(randomEvent.id);
      this.stateManager.updateState({ explorationState: state.explorationState });

      // 执行事件并返回结果
      const eventResults = this.eventHandler.executeRandomEvent(randomEvent, { location: state.location });
      return {
        event: randomEvent,
        results: eventResults
      };
    }

    return null;
  }

  // 处理探索选择
  handleExplorationChoice(choice) {
    const state = this.stateManager.getState();
    if (!state.explorationState) {
      return ['你还没有开始探索。请先输入 explore 开始。'];
    }

    const choiceNum = parseInt(choice);
    const scene = state.explorationState.scene;
    const maxChoices = this.getMaxChoices(scene);

    if (this.isInvalidChoice(choiceNum, maxChoices)) {
      return ['无效选择。请重新选择。'];
    }

    if (choiceNum === 0) {
      return this.endExploration();
    }

    return this.processChoice(choiceNum, scene, state.explorationState);
  }

  // 获取最大选择数
  getMaxChoices(scene) {
    return scene.baseEvents?.length || scene.actions?.length || 0;
  }

  // 检查选择是否无效
  isInvalidChoice(choiceNum, maxChoices) {
    return isNaN(choiceNum) || choiceNum < 0 || choiceNum > maxChoices;
  }

  // 结束探索
  endExploration() {
    // 先获取当前探索状态
    const currentExplorationState = this.stateManager.getState().explorationState;
    console.log('结束探索时的explorationState:', currentExplorationState);
    console.log('结束探索时的collectedItems:', currentExplorationState?.collectedItems);
    const summary = this.generateExplorationSummary(currentExplorationState);
    this.stateManager.updateState({ explorationState: null });
    return summary;
  }

  // 处理选择并执行效果
  processChoice(choiceNum, scene, explorationState) {
    const messages = [];
    const eventData = this.getEventData(choiceNum - 1, scene);

    if (!eventData) return messages;

    messages.push(`你选择了：${eventData.name}`);

    if (eventData.narrative) {
      messages.push(eventData.narrative);
    }

    this.processOutcomes(eventData.outcomes, explorationState, messages);

    explorationState.explorationCount++;
    // 确保collectedItems在状态更新时被保留
    console.log('processChoice结束时的collectedItems:', explorationState.collectedItems);
    this.stateManager.updateState({ explorationState });

    return messages;
  }

  // 获取事件数据
  getEventData(index, scene) {
    if (scene.baseEvents) {
      const eventName = scene.baseEvents[index];
      return {
        name: eventName,
        outcomes: this.generateSimpleOutcomes(eventName)
      };
    } else if (scene.actions) {
      const action = scene.actions[index];
      return {
        name: action.description,
        narrative: action.narrative,
        outcomes: action.outcomes
      };
    }
    return null;
  }

  // 处理结果并更新状态
  processOutcomes(outcomes, explorationState, messages) {
    outcomes.forEach(outcome => {
      if (Math.random() < outcome.prob) {
        const effectMessages = this.executeEffect(outcome);
        messages.push(...effectMessages);

        this.updateExplorationState(outcome, explorationState);
      }
    });
  }

  // 更新探索状态
  updateExplorationState(outcome, explorationState) {
    if (outcome.discovery) {
      explorationState.discovered.push(outcome.discovery);
      if (outcome.clue) {
        explorationState.clues.push(outcome.clue);
      }
    }
    if (outcome.mood) {
      explorationState.mood = outcome.mood;
    }
  }

  // 生成简单结果（使用配置文件）
  generateSimpleOutcomes(eventName) {
    const state = this.stateManager.getState();
    const location = state.location;

    const beastEncounterChance = this.getBeastEncounterChance(eventName, location);

    if (Math.random() < beastEncounterChance) {
      // 生成妖兽并准备战斗
      const beast = generateRandomBeast(location);
      this.pendingBeastEncounter = beast;

      // 直接返回战斗触发消息，标记为特殊类型
      return [{
        prob: 1.0,
        effect: () => ({
          type: 'beast_encounter',
          messages: ['你遭遇了一只妖兽！', `【${beast.name}】`, beast.description, '战斗即将开始...'],
          beast: beast
        })
      }];
    }

    // 使用普通探索结果
    return explorationEventResults[eventName] || [
      { prob: 0.5, effect: () => [{ message: '你找到了一些【灵草】。', isItemGain: true }, { action: 'addItem', itemName: '灵草' }] },
      { prob: 0.8, effect: () => [{ type: 'xp', value: 10 }] },
      { prob: 1.0, effect: () => [{ action: 'updateAttribute', attribute: 'qi', value: Math.max(0, this.stateManager.getAttribute('qi') - 15) }, '遭遇小危险，气血减少15。'] }
    ];
  }

  // 获取妖兽遭遇概率
  getBeastEncounterChance(eventName, location) {
    const state = this.stateManager.getState();
    return beastEncounterRates[location]?.[eventName] || 0.1;
  }

  // 执行效果函数，处理各种游戏逻辑
  executeEffect(outcome) {
    const state = this.stateManager.getState();
    const result = outcome.effect();

    if (result && typeof result === 'object' && result.type === 'beast_encounter') {
      // 特殊处理妖兽遭遇，返回消息数组
      return result.messages || [];
    }

    if (Array.isArray(result)) {
      // 处理消息数组，只保留最重要的消息
      const messages = [];
      const effects = [];

      result.forEach(item => {
        if (typeof item === 'string') {
          // 过滤掉不重要的字符串消息，只保留包含关键信息的消息
          if (this.isImportantMessage(item)) {
            messages.push(item);
          }
        } else if (typeof item === 'object' && item !== null) {
          if (item.message && typeof item.message === 'string') {
            // 新的消息对象格式（通常是物品获得消息）
            messages.push(item);
          } else {
            // 游戏效果对象
            effects.push(item);
          }
        }
      });

      // 执行游戏效果
      effects.forEach(effect => {
        console.log('处理效果:', effect);
        if (effect.type === 'xp') {
          gainXp(state, effect.value);
        } else if (effect.action === 'addItem') {
          console.log('添加物品到背包:', effect.itemName, effect.count || 1);
          this.stateManager.addItemToBag(effect.itemName, effect.count || 1);
          // 记录本次探索获得的物品数量
          const explorationState = this.stateManager.getState().explorationState;
          console.log('explorationState:', explorationState);
          console.log('collectedItems before:', explorationState?.collectedItems);
          if (explorationState) {
            const count = effect.count || 1;
            if (!explorationState.collectedItems[effect.itemName]) {
              explorationState.collectedItems[effect.itemName] = 0;
            }
            explorationState.collectedItems[effect.itemName] += count;
            console.log('added to collectedItems:', effect.itemName, count);
            console.log('collectedItems after:', explorationState.collectedItems);
          } else {
            console.log('explorationState is null');
          }
        } else if (effect.action === 'updateAttribute') {
          this.stateManager.updateAttribute(effect.attribute, effect.value);
        } else if (effect.action === 'increaseAttribute') {
          this.stateManager.increaseAttribute(effect.attribute, effect.value);
        }
      });

      return messages;
    }

    return [];
  }

  // 判断消息是否重要（过滤掉冗余信息）
  isImportantMessage(message) {
    if (!message || typeof message !== 'string') return false;

    // 过滤掉这些不重要的消息
    const unimportantPatterns = [
      '你小心翼翼地探索着', // 重复的场景描述
      '这里充满了未知', // 重复的场景描述
      '你环顾四周', // 太泛化的描述
      '空气中弥漫着', // 太泛化的描述
      '你听到一些声音', // 太泛化的描述
      '似乎一切都很平静', // 太泛化的描述
      '你什么都没有发现', // 负反馈消息
      '只是普通的石头', // 无价值发现
      '你感到有些疲惫', // 常见负面效果
      '气血减少', // 气血变化已在其他地方显示
      '你休息了一会儿', // 太常见的描述
    ];

    // 如果消息包含任何不重要的模式，返回false
    return !unimportantPatterns.some(pattern => message.includes(pattern));
  }

  // 生成探索总结
  generateExplorationSummary(explorationState) {
    const messages = [];

    console.log('生成探索总结，explorationState:', explorationState);
    console.log('collectedItems:', explorationState.collectedItems);

    messages.push('=== 机缘结束 ===');
    messages.push(`本次探索心情：${UtilsManager.getMoodDescription(explorationState.mood)}`);

    // 只显示本次探索获得的可以进入背包的物品
    const collectedItemNames = Object.keys(explorationState.collectedItems || {});
    if (collectedItemNames.length > 0) {
      messages.push(`本次探索获得物品：${collectedItemNames.join('、')}`);
      messages.push('');
      messages.push('获得物品详情：');
      collectedItemNames.forEach((item, idx) => {
        const count = explorationState.collectedItems[item] || 0;
        messages.push(`${idx + 1}. ${item} ×${count}`);
      });
    } else {
      messages.push('本次探索没有获得物品。');
      console.log('collectedItems为空');
    }

    // Update exploration stats
    const state = this.stateManager.getState();
    state.totalExplorationCount = (state.totalExplorationCount || 0) + 1;

    this.stateManager.updateState({
      totalExplorationCount: state.totalExplorationCount
    });

    messages.push('');
    messages.push(`累计探索次数：${state.totalExplorationCount}`);
    messages.push('继续探索，发现更多秘密吧！');

    return messages;
  }

  // 显示发现
  showDiscoveries() {
    const state = this.stateManager.getState();
    if (!state.totalDiscoveries || state.totalDiscoveries.length === 0) {
      return ['你还没有任何发现。去探索世界吧！'];
    }

    const messages = [];
    messages.push('=== 修仙探索日志 ===');
    messages.push(`累计探索次数：${state.totalExplorationCount || 0}`);
    messages.push(`发现总数：${state.totalDiscoveries.length}`);
    messages.push('');

    // 按地点分类显示发现
    const discoveriesByLocation = {};
    state.totalDiscoveries.forEach(discovery => {
      let location = '未知';
      if (discovery.includes('洞府') || discovery.includes('符文') || discovery.includes('玉石')) {
        location = '洞府';
      } else if (discovery.includes('后山') || discovery.includes('伐木') || discovery.includes('铁矿')) {
        location = '后山';
      } else if (discovery.includes('秘境') || discovery.includes('灵花') || discovery.includes('灵石')) {
        location = '秘境';
      } else if (discovery.includes('云海') || discovery.includes('云') || discovery.includes('法则')) {
        location = '云海';
      }

      if (!discoveriesByLocation[location]) {
        discoveriesByLocation[location] = [];
      }
      discoveriesByLocation[location].push(discovery);
    });

    for (const [location, discoveries] of Object.entries(discoveriesByLocation)) {
      messages.push(`${location}发现 (${discoveries.length}项)：`);
      discoveries.forEach((disc, idx) => {
        messages.push(`  ${idx + 1}. ${disc}`);
      });
      messages.push('');
    }

    const uniqueDiscoveries = new Set(state.totalDiscoveries);
    messages.push(`独特发现：${uniqueDiscoveries.size}/${state.totalDiscoveries.length} 项`);
    messages.push('继续探索，发现更多秘密吧！');

    return messages;
  }

  // 获取事件历史
  getEventHistory() {
    return this.eventHandler.getEventHistory();
  }

  // 显示彩蛋信息
  showEasterEggs() {
    const messages = ['=== 隐藏彩蛋 ==='];
    // 这里可以显示已解锁的彩蛋
    messages.push('彩蛋系统正在开发中...');
    return messages;
  }

  // 重置探索状态（用于测试）
  resetExploration() {
    this.stateManager.updateState({
      explorationState: null,
      totalDiscoveries: [],
      totalExplorationCount: 0,
      explorationEvents: []
    });
    return ['探索状态已重置。'];
  }

  // 开始妖兽战斗
  startBeastBattle() {
    if (!this.pendingBeastEncounter) {
      return null;
    }

    const state = this.stateManager.getState();

    // Convert skills object to array if needed
    let skillsArray = [];
    if (state.skills) {
      if (Array.isArray(state.skills)) {
        skillsArray = state.skills;
      } else if (typeof state.skills === 'object') {
        skillsArray = Object.keys(state.skills);
      }
    }

    // 使用考虑状态效果后的属性值
    const player = {
      qi: this.stateManager.getAttribute('qi'),
      maxQi: this.stateManager.getAttribute('maxQi'), // 传递最大血量
      attack: this.stateManager.getAttribute('attack'),
      defense: this.stateManager.getAttribute('defense'),
      speed: 10, // 基础速度
      skills: skillsArray,
      name: state.name
    };

    const battleState = this.battleManager.startBattle(player, this.pendingBeastEncounter);
    this.pendingBeastEncounter = null; // 清除待处理的遭遇

    return battleState;
  }

  // 执行战斗动作
  executeBattleAction(action, skill, availableSkills) {
    switch (action) {
      case 'attack':
        // 临时更新玩家技能以确保验证通过
        if (this.battleManager.currentBattle && availableSkills) {
          this.battleManager.currentBattle.player.skills = availableSkills;
        }
        return this.battleManager.playerAttack(skill);
      case 'flee':
        return this.battleManager.playerFlee();
      default:
        return { success: false, message: '未知的战斗动作' };
    }
  }

  // 获取战斗状态
  getBattleState() {
    return this.battleManager.getBattleState();
  }

  // 检查是否有妖兽遭遇
  hasPendingBeastEncounter() {
    return this.pendingBeastEncounter !== null;
  }

  // 获取待处理的妖兽
  getPendingBeast() {
    return this.pendingBeastEncounter;
  }

  // 清除妖兽遭遇
  clearPendingBeastEncounter() {
    this.pendingBeastEncounter = null;
  }

  // 检查是否有正在进行的战斗
  isInBattle() {
    return this.battleManager.isInBattle();
  }
}
